import keyBy from 'lodash/keyBy';

import { narrativeErrorStatuses, narrativeStatus } from '@helpers';
import { getFormattedLongDate } from '@utilities';

import { transformPrompt } from '@src/app/pages/Dashboard/components/Builder/utilities/helpers.js';

/**
 * transformColumn - capitalizes Header for FE table purposes
 * @param {Object} column
 * @param {String} column.accessor
 * @param {String?} [column.format]
 * @param {String} column.header
 * @param {Boolean?} [column.index_col]
 * @param {Number?} [column.width]
 * @return {Object}
 */
export const transformColumn = ({ accessor, format, Header, index_col: indexCol, width }) => ({
  accessor,
  format,
  Header,
  indexCol,
  width,
});

/**
 * transformDraftDataset
 * @param {Object} dataset
 * @return {Object}
 */
const transformDraftDataset = (dataset = {}) => {
  if (!dataset) return null;
  const { clean_read_as, cleanReadAs, read_as, readAs, ...rest } = dataset;

  return {
    ...rest,
    cleanReadAs: clean_read_as ?? cleanReadAs,
    readAs: read_as ?? readAs,
  };
};

/**
 * extractDraftTopics
 * @param {Object|Array} payload
 * @return {Array}
 */
const extractDraftTopics = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.visualizations)) return payload.visualizations;
  return [];
};

/**
 * transformDraftTopicsData
 * @param {Object|Array} draftTopics
 * @return {Object}
 */
export const transformDraftTopicsData = (draftTopics) => {
  const topics = extractDraftTopics(draftTopics);
  if (!topics.length) return {};

  return topics.reduce((acc, draftTopic) => {
    const componentId = draftTopic?.component_id || draftTopic?.componentId;
    if (!componentId) return acc;

    const viz = draftTopic?.viz || draftTopic?.visualization || {};
    const title = viz?.title || draftTopic?.title || null;
    const cleanTitle = viz?.clean_title || viz?.cleanTitle || null;
    const datasets = Array.isArray(viz?.datasets)
      ? viz.datasets.map((dataset) => transformDraftDataset(dataset)).filter(Boolean)
      : [];
    const dataset = datasets[0] || null;

    acc[componentId] = {
      cleanTitle,
      componentId,
      dataset,
      sampleSize: viz?.sample_size,
      title,
    };

    return acc;
  }, {});
};

/**
 * transformTopic
 * @param {Object} topic
 * @return {Object}
 */
export const transformTopic = (topic) => {
  const { feedback, flow_id, is_hidden, title, topic_id, viz, ...remainingTopic } = topic;
  const newDatasets = viz?.datasets?.map((dataset) => {
    const { clean_read_as, read_as, ...remainingDataset } = dataset;
    return {
      cleanReadAs: clean_read_as,
      readAs: read_as,
      ...remainingDataset,
    };
  });
  const newVisualization = { ...viz, datasets: newDatasets };

  return {
    error: null,
    feedback: {
      like: feedback?.like,
      reason: feedback?.reason,
      reasonExplanation: feedback?.reason_explanation,
    },
    flowId: flow_id,
    isHidden: is_hidden,
    loading: false,
    title,
    topicId: topic_id,
    visualization: newVisualization,
    ...remainingTopic,
  };
};

/**
 * transformFlow
 * @param {Object} flow
 * @return {Object}
 */
export const transformFlow = (flow) => {
  const { answers, narrative_name, queue_datetime, topics = [], ...rest } = flow;

  const createdDatetime = queue_datetime ? getFormattedLongDate(new Date(queue_datetime)) : null;

  const transformedTopics = topics.map((topic) => {
    const { feedback, is_hidden, topic_id, ...remainingTopic } = topic;
    return {
      feedback: {
        like: feedback?.like,
        reason: feedback?.reason,
        reasonExplanation: feedback?.reason_explanation,
      },
      isHidden: is_hidden,
      topicId: topic_id,
      ...remainingTopic,
    };
  });

  // Keep in parity with repromptAnswers in ModalBuildNarrative to ensure propper submission for reprompt
  const {
    subscribed_categories: subscribedCategories,
    subscribed_channel: subscribedChannels,
    ...restAnswers
  } = answers;

  return {
    ...rest,
    answers: {
      subscribedCategories,
      subscribedChannels,
      ...restAnswers,
    },
    createdDatetime,
    narrativeName: narrative_name,
    topics: transformedTopics,
  };
};

/**
 * transforms and formats narrative data V2
 * @param  {Object} data
 * @return {Object}
 */
export const transformNarrativeItemData = (data) => {
  return {
    createdDateTime: new Date(data?.queue_datetime).toLocaleDateString('en-us', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }),
    creator: data?.creator,
    hasException: narrativeErrorStatuses.includes(data?.status),
    id: data?.id,
    isCompleted: data?.status === narrativeStatus.COMPLETED,
    isItem: true,
    isOwner: data?.is_owner ?? true,
    isShared: data?.is_shared,
    name: data?.name,
    narrativeId: data?.narrative_id,
    narrativeName: data?.narrative_name,
    numberCompleteTopics: data?.complete_flow_topics,
    numberIncompleteTopics: data?.incomplete_flow_topics,
    permissions: {
      canFavorite: data?.document?.can_favorite,
      canReprompt: data?.document?.can_reprompt,
      canShare: data?.document?.can_share,
    },
    status: data?.status,
  };
};

/**
 * transforms and formats narrative list items
 * @param  {Object} data
 * @return {Object}
 */
export const transformNarrativeItemsData = (data) => {
  return {
    items: data?.items?.map((item) => {
      return transformNarrativeItemData(item);
    }),
    page: data?.page,
    size: data?.size,
    total: data?.total,
  };
};

/** * transforms narrative type data
 * @param {Object} data
 * @return {Object}
 */
export const transformNarrativeType = (data) => {
  const {
    default_answers,
    hierarchy_search_prompts,
    prompt_grouping,
    prompts,
    same_level_prompts,
    ...remainingData
  } = data;

  return {
    ...remainingData,
    defaultAnswers: default_answers,
    promptGrouping: prompt_grouping,
    prompts: prompts.map(transformNarrativePrompt),
    sameLevelPrompts: same_level_prompts,
    searchPrompts: hierarchy_search_prompts,
  };
};

const transformNarrativePrompt = ({
  advanced: isAdvanced,
  description,
  empty_value: emptyValue,
  id: promptKey,
  is_prefix_included: isPrefixIncluded,
  isPinnable,
  max: maxValues,
  min: minAnswers,
  module,
  name,
  read_only: isReadOnly,
  required: isRequired,
  search_enabled: isSearchEnabled,
  shortTitle,
  sort_enabled: isSortEnabled,
  title,
  type,
}) => {
  const isDateRange = promptKey === 'date_range';
  return {
    ...(module?.attributes?.length && {
      attributes: module?.attributes.map(
        ({ creator, description, id, is_custom: isCustom, is_fixed: isFixed, name, type }) => ({
          creator,
          description,
          id,
          isCustom,
          isFixed,
          name,
          type,
        })
      ),
    }),
    ...(isDateRange
      ? {
          dateOptions: module?.date_options?.map(
            ({ options = [], time_frame: timeFrame, tooltip }) => ({
              options: options.map(({ date_range, label }) => {
                return {
                  label,
                  value: date_range,
                };
              }),
              timeFrame,
              type: tooltip,
            })
          ),
        }
      : {}),
    description,
    emptyValue,
    hasCustomDateOption: isDateRange,
    id: promptKey,
    isAdvanced,
    isDateRange,
    isPinnable,
    isPrefixIncluded,
    isReadOnly,
    isRequired,
    isSearchEnabled,
    isSortEnabled,
    maxValues,
    minAnswers,
    name,
    promptKey,
    shortTitle,
    title,
    type,
  };
};

/** * transforms narrative type data
 * @param {Object} data
 * @return {Object}
 */
export const transformCreateNarrativeResponse = (data) => {
  return {
    flowId: data?.flow_id ?? null,
  };
};

/**
 * transforms and forms narrative prompts
 * @param  {Object} data
 * @return {Object}
 */
export const transformNarrativePrompts = (data) => {
  return {
    ...keyBy(
      data?.prompts?.map((prompt) => transformPrompt(prompt)),
      'id'
    ),
    shopper_group: {
      name: 'Groups',
    },
  };
};

/** Transforms narrative topics data
 * @param {Array} topics
 * @returns {Array}
 */

export const transformNarrativeTopics = (topics) => {
  return topics.map((topic) => {
    const { is_favorited, viz_type, ...rest } = topic;
    return {
      ...rest,
      isFavorited: is_favorited,
      vizType: viz_type,
    };
  });
};
